import React, { useEffect, useState } from "react";
import { Box, Typography, Button, TextField, Snackbar, Alert, CircularProgress, Grid, Chip, Paper, Divider } from "@mui/material";
import { useTheme } from "@emotion/react";
import { TipsAndUpdatesOutlined, SendOutlined, InboxOutlined, HistoryOutlined, AttachFileOutlined, InsertDriveFileOutlined, DownloadOutlined, AccessTimeOutlined, CheckCircleOutline, DescriptionOutlined } from "@mui/icons-material";
import { alpha } from "@mui/system";
import useStyles from "./styles";
import apiCliente from "../../../services/apiCliente";
import StyledSuggestionRatingComponent from "../../../components/styledSuggestionRatingComponent";

const ModernClientHeaderIcon = ({ icon: IconComponent, color = "primary", theme }) => (
  <Box
    sx={{
      width: 48,
      height: 48,
      borderRadius: "12px",
      bgcolor: alpha(theme.palette[color].main, 0.12),
      border: `1px solid ${alpha(theme.palette[color].main, 0.25)}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    }}
  >
    <IconComponent sx={{ color: theme.palette[color].main, fontSize: 26 }} />
  </Box>
);

const ModernClientHistoryItemHeader = ({ ans, theme }) => {
  const formattedDate = ans.createAt
    ? new Date(ans.createAt).toLocaleString("pt-BR", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
        pb: 1.8,
        borderBottom: `1px dashed ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "10px",
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <TipsAndUpdatesOutlined sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.2 }}>
            {ans.formTitle || "Sugestão Enviada"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.4 }}>
            <Chip
              icon={<CheckCircleOutline sx={{ fontSize: "14px !important", color: "inherit !important", ml: "6px !important" }} />}
              label="Registrada com sucesso"
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 600,
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: theme.palette.mode === "dark" ? theme.palette.success.light : theme.palette.success.dark,
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: "6px",
              }}
            />
          </Box>
        </Box>
      </Box>

      {formattedDate && (
        <Chip
          icon={<AccessTimeOutlined sx={{ fontSize: "14px !important", color: "inherit !important", ml: "6px !important" }} />}
          label={formattedDate}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.72rem",
            fontWeight: 600,
            bgcolor: alpha(theme.palette.text.secondary, 0.08),
            color: theme.palette.text.secondary,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            borderRadius: "6px",
          }}
        />
      )}
    </Box>
  );
};

const ModernClientAttachmentCard = ({ anx, downloadAnexo, theme }) => {
  const ext =
    anx?.nome?.split(".")?.pop()?.toUpperCase() ||
    anx?.mimeType?.split("/")?.pop()?.toUpperCase() ||
    "ARQUIVO";

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 1.2,
        pr: 1.5,
        borderRadius: "10px",
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        minWidth: { xs: "100%", sm: "240px" },
        flex: "1 1 240px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, overflow: "hidden" }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "8px",
            bgcolor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <InsertDriveFileOutlined sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Typography
            variant="body2"
            title={anx.nome}
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {anx.nome || "Anexo sem nome"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.2 }}>
            <Chip
              label={ext}
              size="small"
              sx={{
                height: 16,
                fontSize: "0.62rem",
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                borderRadius: "4px",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={<DownloadOutlined sx={{ fontSize: "15px !important" }} />}
        onClick={() => downloadAnexo(anx)}
        sx={{
          borderRadius: "6px",
          textTransform: "none",
          fontWeight: 600,
          px: 1.5,
          py: 0.4,
          boxShadow: "none",
          flexShrink: 0,
        }}
      >
        Baixar
      </Button>
    </Paper>
  );
};

export default function SugestoesCliente() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [myAnswers, setMyAnswers] = useState([]);
  const [snackbar, setSnackbar] = useState(null);
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);

  useEffect(() => {
    fetchForm();
  }, []);

  const fetchForm = async () => {
    try {
      setLoading(true);
      const [formResponse, historyResponse] = await Promise.all([
        apiCliente.get("/client/find/suggestionForm"),
        apiCliente.get("/client/find/mySuggestionAnswers").catch(() => ({ data: [] })),
      ]);

      if (Array.isArray(historyResponse?.data)) {
        setMyAnswers(historyResponse.data);
      } else {
        setMyAnswers([]);
      }

      if (formResponse?.data?.alreadyAnswered) {
        setAlreadyAnswered(true);
      } else if (formResponse?.data?.noActiveForm || !formResponse?.data) {
        setForm(null);
      } else {
        setForm(formResponse.data);
      }
    } catch (error) {
      console.error("Erro ao buscar formulário de sugestão:", error);
      // Fallback amigável de erro se a migration ainda não foi rodada ou o backend estiver fora
      setForm(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => {
      const existing = prev.find((a) => a.questionId === questionId);
      if (existing) {
        return prev.map((a) => (a.questionId === questionId ? { ...a, answer: value } : a));
      }
      return [...prev, { questionId, answer: value }];
    });
  };

  const toggleChipSelecionado = (questionId, optionValue) => {
    setAnswers((prev) => {
      const existing = prev.find((a) => a.questionId === questionId);
      let currentArray = existing && Array.isArray(existing.answer) ? existing.answer : [];
      
      if (currentArray.includes(optionValue)) {
        currentArray = currentArray.filter((item) => item !== optionValue);
      } else {
        currentArray = [...currentArray, optionValue];
      }
      
      if (existing) {
        return prev.map((a) => (a.questionId === questionId ? { ...a, answer: currentArray } : a));
      }
      return [...prev, { questionId, answer: currentArray }];
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      const newFiles = await Promise.all(
        files.map(async (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type || "application/octet-stream",
                size: file.size,
                base64: reader.result,
              });
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
          });
        })
      );
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    } catch (err) {
      console.error("Erro ao processar arquivo em anexo:", err);
      setSnackbar({
        children: "Erro ao carregar arquivo em anexo.",
        severity: "error",
      });
    } finally {
      e.target.value = null;
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const downloadAnexo = (anexo) => {
    if (!anexo?.dadosBase64) return;
    const link = document.createElement("a");
    link.href = `data:${anexo.mimeType || "application/octet-stream"};base64,${anexo.dadosBase64}`;
    link.download = anexo.nome || "anexo";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    // Validar respostas obrigatórias
    const missing = form?.questions?.some((q) => {
      const found = answers.find((a) => a.questionId === q.id);
      if (!found) return true;
      if (Array.isArray(found.answer) && found.answer.length === 0) return true;
      if (typeof found.answer === "string" && !found.answer.trim()) return true;
      return false;
    });

    if (missing) {
      setSnackbar({
        children: "Por favor, responda todas as perguntas antes de enviar.",
        severity: "warning",
      });
      return;
    }

    try {
      setSubmitting(true);
      await apiCliente.post("/client/create/suggestionAnswer", {
        formId: form.id,
        answers,
        anexos: selectedFiles.map((file) => ({
          nome: file.name,
          mimeType: file.type,
          dadosBase64: file.base64,
        })),
      });
      setSnackbar({
        children: "Sugestão enviada com sucesso! Obrigado por contribuir.",
        severity: "success",
      });
      setSelectedFiles([]);
      setAnswers([]);
      setAlreadyAnswered(true);
      await fetchForm();
    } catch (error) {
      console.error("Erro ao enviar sugestão:", error);
      const msg = error?.response?.data?.message || "Erro ao enviar a sugestão. Tente novamente mais tarde.";
      setSnackbar({
        children: msg,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderMinhasSugestoes = () => (
    <Grid item xs={12} md={6}>
      <Box sx={styles.formCard}>
        <Box sx={styles.header}>
          <ModernClientHeaderIcon icon={HistoryOutlined} color="primary" theme={theme} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
            Minhas Sugestões
          </Typography>
        </Box>

        <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column", gap: 2.2, overflowY: "auto", maxHeight: "680px" }}>
          {myAnswers && myAnswers.length > 0 ? (
            myAnswers.map((ans) => (
              <Paper
                key={ans.id}
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  background: theme.palette.background.default,
                  borderColor: theme.palette.divider,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.8,
                }}
              >
                <ModernClientHistoryItemHeader ans={ans} theme={theme} />

                {ans.ratings?.map((r, idx) => (
                  <Box key={idx}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                      {r.question || "Avaliação"}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }}>
                      {r.answer ? `${r.answer} de 5 estrelas` : "Não respondido"}
                    </Typography>
                  </Box>
                ))}

                {ans.comments?.map((c, idx) => (
                  <Box key={idx}>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                      {c.question || "Comentário"}:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5, fontWeight: 500 }}>
                      {c.answer || "Nenhum comentário"}
                    </Typography>
                  </Box>
                ))}

                {ans.options?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, display: "block", mb: 0.6 }}>
                      Opções selecionadas:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                      {ans.options.map((o, idx) => (
                        <Chip
                          key={idx}
                          label={o.option}
                          size="small"
                          sx={{
                            borderRadius: "6px",
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {ans.anexos?.length > 0 && (
                  <Box sx={{ mt: 1, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "6px",
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DescriptionOutlined sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                        Arquivos Anexados ({ans.anexos.length}):
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
                      {ans.anexos.map((anx) => (
                        <ModernClientAttachmentCard
                          key={anx.id}
                          anx={anx}
                          downloadAnexo={downloadAnexo}
                          theme={theme}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            ))
          ) : (
            <Box sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              background: alpha(theme.palette.primary.main, 0.05),
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
              borderRadius: "12px",
              p: 4,
              my: "auto"
            }}>
              <InboxOutlined sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2, opacity: 0.7 }} />
              <Typography variant="h6" color="textPrimary" gutterBottom>
                Nenhuma sugestão enviada
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Você ainda não enviou nenhuma sugestão. Utilize o formulário ao lado para compartilhar suas ideias conosco!
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ ...styles.container, justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Grid container spacing={1} sx={{ width: "100%"}}>
        {/* Lado Esquerdo - Formulário */}
        <Grid item xs={12} md={6}>
          {alreadyAnswered || !form ? (
            <Box sx={{ ...styles.formCard, textAlign: "center", p: 4, height: "100%", justifyContent: "center" }}>
              <TipsAndUpdatesOutlined sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2, mx: "auto" }} />
              <Typography variant="h5" gutterBottom>
                {alreadyAnswered ? "Sugestão Recebida!" : "Nenhum formulário ativo"}
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 3 }}>
                {alreadyAnswered
                  ? "Agradecemos sua contribuição! Suas ideias nos ajudam a melhorar o sistema continuamente."
                  : "No momento não há nenhum canal de sugestões aberto. Volte novamente mais tarde!"}
              </Typography>
              {alreadyAnswered && form && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setAlreadyAnswered(false);
                    setAnswers([]);
                    setSelectedFiles([]);
                  }}
                  sx={{ mt: 2, fontWeight: 600, borderRadius: "8px", px: 4 }}
                >
                  Enviar Nova Sugestão
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={styles.formCard}>
              <Box sx={styles.header}>
               
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
                    {form.title || "Sugestões de Melhoria para a Plataforma"}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9, mt: 0.2 }}>
                    Deixe sua sugestão e nos ajude a evoluir a plataforma continuamente.
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.content}>
                {form.questions?.map((q) => (
                  <Box key={q.id} sx={styles.questionBox}>
                    <Typography sx={styles.questionTitle}>
                      {q.question}
                      <span style={{ color: theme.palette.error.main, marginLeft: 4 }}>*</span>
                    </Typography>

                    {q.type === 0 && (
                      <StyledSuggestionRatingComponent
                        questionId={q.id}
                        rating={answers}
                        setRating={setAnswers}
                      />
                    )}

                    {q.type === 1 && (
                      <TextField
                        multiline
                        rows={4}
                        variant="outlined"
                        placeholder="Escreva sua resposta aqui..."
                        fullWidth
                        value={answers.find((a) => a.questionId === q.id)?.answer || ""}
                        onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            background: theme.palette.background.paper,
                            borderRadius: "10px",
                          },
                        }}
                      />
                    )}

                    {q.type === 2 && (
                      <Paper variant="outlined" sx={{
                        width: "100%",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        padding: "14px",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        mt: 1,
                        borderRadius: "10px",
                        background: theme.palette.background.default,
                        borderColor: theme.palette.divider
                      }}>
                        {q.options?.map((option) => {
                          const optValue = option?.option || option?.value || option;
                          const currentAnswer = answers.find((a) => a.questionId === q.id)?.answer || [];
                          const isSelected = Array.isArray(currentAnswer) && currentAnswer.includes(optValue);
                          
                          return (
                            <Chip
                              key={optValue}
                              label={optValue}
                              size="medium"
                              clickable
                              onClick={() => toggleChipSelecionado(q.id, optValue)}
                              color={isSelected ? "primary" : "default"}
                              variant={isSelected ? "filled" : "outlined"}
                              sx={{
                                transition: "all 0.2s",
                                borderRadius: "8px",
                                fontWeight: 600,
                                ...(isSelected
                                  ? {
                                      bgcolor: theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                    }
                                  : {
                                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                                      borderColor: alpha(theme.palette.primary.main, 0.2),
                                    }),
                              }}
                            />
                          );
                        })}
                      </Paper>
                    )}
                  </Box>
                ))}

                {/* Seção de Anexos (Qualquer tipo de arquivo) */}
                <Box sx={styles.questionBox}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AttachFileOutlined sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography sx={styles.questionTitle}>
                      Anexar Arquivos
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.3 }}>
                    Você pode anexar qualquer tipo de arquivo (imagens, documentos, planilhas, PDFs, logs, etc.) para ilustrar sua sugestão.
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 1 }}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<AttachFileOutlined />}
                      size="small"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                        px: 2,
                        py: 0.8,
                        boxShadow: "none",
                      }}
                    >
                      Adicionar Arquivos
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={handleFileChange}
                      />
                    </Button>
                  </Box>

                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
                      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, display: "block", mb: 1, textTransform: "uppercase" }}>
                        Arquivos Pronto Para Envio ({selectedFiles.length}):
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
                        {selectedFiles.map((file, index) => {
                          const ext = file.name?.split(".")?.pop()?.toUpperCase() || "ARQUIVO";
                          return (
                            <Paper
                              key={index}
                              variant="outlined"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.2,
                                p: 1,
                                pr: 1.5,
                                borderRadius: "8px",
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? alpha(theme.palette.primary.main, 0.05)
                                    : alpha(theme.palette.primary.main, 0.03),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "6px",
                                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                                  color: theme.palette.primary.main,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <InsertDriveFileOutlined sx={{ fontSize: 18 }} />
                              </Box>
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {file.name}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
                                  <Chip
                                    label={ext}
                                    size="small"
                                    sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, borderRadius: "4px" }}
                                  />
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: "0.68rem" }}>
                                    {(file.size / 1024).toFixed(1)} KB
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label="Remover"
                                size="small"
                                clickable
                                onClick={() => removeFile(index)}
                                color="error"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, borderRadius: "6px", ml: 0.5 }}
                              />
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={styles.footer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={styles.buttonSubmit}
                  endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendOutlined />}
                >
                  {submitting ? "Enviando..." : "Enviar Sugestão"}
                </Button>
              </Box>
            </Box>
          )}
        </Grid>

        {/* Lado Direito - Minhas Sugestões */}
        {renderMinhasSugestoes()}
      </Grid>

      {!!snackbar && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.children}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}

